import React, { useEffect, useRef, useState } from 'react'
import { CircularProgress } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useCompanySpaces } from '../queries/useCompanyQueries'
import { adjustColor } from '../theme/ajustColor'
import { useUI } from '../hooks/useUI'

interface PlacesAutocompleteProps {
  value: string
  onChange: (value: string, placeId?: string) => void
  label: string
  required?: boolean
  helperText?: string
  error?: boolean
}

const PlacesAutocomplete: React.FC<PlacesAutocompleteProps> = ({
  value,
  onChange,
  label,
  required = false,
  helperText,
  error: externalError
}) => {
  const { t } = useTranslation()
  const [predictions, setPredictions] = useState<google.maps.places.AutocompletePrediction[]>([])
  const [inputValue, setInputValue] = useState(value)
  const [isOpen, setIsOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [touched, setTouched] = useState(false)
  const autocompleteService = useRef<google.maps.places.AutocompleteService | null>(null)
  const componentRef = useRef<HTMLDivElement>(null)
  const { darkMode } = useUI()
  const { data: companyData } = useCompanySpaces()

  // Generar paleta de colores basada en el modo
  const primaryColor = companyData?.body?.content[0]?.color || '#0A3A7A'
  const colors = adjustColor(primaryColor, darkMode ? 'dark' : 'light')

  useEffect(() => {
    if (window.google && !autocompleteService.current) {
      autocompleteService.current = new google.maps.places.AutocompleteService()
    }
  }, [])

  useEffect(() => {
    setInputValue(value)
  }, [value])

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (componentRef.current && !componentRef.current.contains(event.target as Node)) {
        setIsOpen(false)
        setInputValue(value)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [value])

  const handleInputChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value
    setInputValue(newValue)
    setError(null)
    setTouched(true)

    if (newValue.length > 2 && autocompleteService.current) {
      setIsLoading(true)
      try {
        const response = await autocompleteService.current.getPlacePredictions({
          input: newValue,
          componentRestrictions: {
            country: 'cr'
          },
          types: ['establishment'],
          language: 'es',
          region: 'cr'
        })
        setPredictions(response?.predictions || [])
        setIsOpen(true)
        setIsLoading(false)
      } catch (error) {
        console.error('Error fetching predictions:', error)
        setError(t('errorSearchingPlaces'))
        setPredictions([])
        setIsLoading(false)
      }
    } else if (newValue.length > 0 && newValue.length <= 2) {
      setError(t('enterAtLeast3Chars'))
      setPredictions([])
      setIsLoading(false)
    } else {
      setPredictions([])
      setIsOpen(false)
      setIsLoading(false)
    }
  }

  const handlePredictionClick = (prediction: google.maps.places.AutocompletePrediction) => {
    onChange(prediction.description, prediction.place_id)
    setPredictions([])
    setIsOpen(false)
  }

  const hasError = externalError || (touched && required && !inputValue) || !!error
  const errorMessage = error || (touched && required && !inputValue ? t('requiredField') : '')

  return (
    <div ref={componentRef} className="relative w-full">
      {label && (
        <label style={{ color: colors?.text }} className="block text-sm font-medium mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          required={required}
          style={{
            backgroundColor: colors?.background,
            borderColor: colors.dark,
            color: colors?.text
          }}
          className={`w-full px-3 py-2 border rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 ${
            hasError ? 'border-red-500' : ''
          }`}
        />
        {isLoading && (
          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
            <CircularProgress size={20} />
          </div>
        )}
        {!hasError && helperText && (
          <p className="mt-1 text-sm" style={{ color: colors?.text }}>
            {helperText}
          </p>
        )}
      </div>

      {isOpen && predictions.length > 0 && (
        <div
          className="absolute z-50 w-full mt-1 rounded-md shadow-lg"
          style={{
            backgroundColor: colors?.background,
            borderColor: darkMode ? colors?.dark : colors?.light,
            border: '1px solid'
          }}
        >
          {predictions.map((prediction) => (
            <div
              key={prediction.place_id}
              onClick={() => handlePredictionClick(prediction)}
              className="px-4 py-2 cursor-pointer hover:bg-black hover:bg-opacity-5"
              style={{ color: colors?.text }}
            >
              <p className="text-sm">{prediction.description}</p>
            </div>
          ))}
        </div>
      )}

      {hasError && errorMessage && <p className="mt-1 text-sm text-red-600">{errorMessage}</p>}
    </div>
  )
}

export default PlacesAutocomplete
